import { Box, Typography, Alert, Button } from "@mui/material";
import Grid from "@mui/material/Grid";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import EventIcon from "@mui/icons-material/Event";
import RateReviewIcon from "@mui/icons-material/RateReview";
import InsightsIcon from "@mui/icons-material/Insights";
import GroupIcon from "@mui/icons-material/Group";
import { useTranslation } from "react-i18next";
import {
  Navigate,
  useSearchParams,
  Link as RouterLink,
} from "react-router-dom";
import { GoogleLoginButton } from "@components/GoogleLoginButton";
import { useAuth } from "@context/AuthContext";

export default function Login() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated, loading } = useAuth();
  const authFailed = searchParams.get("error") === "auth_failed";
  const features = [
    {
      icon: <EventIcon sx={{ color: "primary.main" }} />,
      title: t("auth.feature1Title"),
      desc: t("auth.feature1Desc"),
    },
    {
      icon: <RateReviewIcon sx={{ color: "secondary.main" }} />,
      title: t("auth.feature2Title"),
      desc: t("auth.feature2Desc"),
    },
    {
      icon: <InsightsIcon sx={{ color: "success.main" }} />,
      title: t("auth.feature3Title"),
      desc: t("auth.feature3Desc"),
    },
    {
      icon: <GroupIcon sx={{ color: "warning.main" }} />,
      title: t("auth.feature4Title"),
      desc: t("auth.feature4Desc"),
    },
  ];

  if (!loading && isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <Box sx={{ py: { xs: 4, md: 8 }, px: { xs: 2, sm: 3 } }}>
      <Grid
        container
        spacing={{ xs: 3, md: 6 }}
        sx={{
          alignItems: "center",
        }}
      >
        <Grid size={{ xs: 12, md: 5 }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: 3,
              bgcolor: "primary.main",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 3,
            }}
          >
            <RestaurantMenuIcon sx={{ fontSize: 48, color: "#fff" }} />
          </Box>

          <Typography
            variant="h3"
            gutterBottom
            sx={{
              fontWeight: 700,
              fontSize: { xs: "2rem", md: "2.8rem" },
            }}
          >
            {t("auth.welcome")}
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: "text.secondary",
              mb: 1,
              maxWidth: 460,
            }}
          >
            {t("auth.tagline")}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              mb: 2.5,
              maxWidth: 460,
            }}
          >
            {t("auth.signInSubtitle")}
          </Typography>

          {authFailed && (
            <Alert
              severity="error"
              sx={{ mb: 3, maxWidth: 460, textAlign: "left", width: "100%" }}
              onClose={() => {
                searchParams.delete("error");
                setSearchParams(searchParams, { replace: true });
              }}
            >
              {t("auth.signInFailed")}
            </Alert>
          )}

          <GoogleLoginButton />
          <Button
            component={RouterLink}
            to="/preview"
            variant="outlined"
            size="large"
            sx={{
              mt: 1.5,
              py: 1.5,
              px: 4,
              fontSize: 16,
              textAlign: "center",
              justifyContent: "center",
              borderColor: "divider",
              color: "text.primary",
              "&:hover": {
                borderColor: "primary.main",
                backgroundColor: "action.hover",
              },
            }}
          >
            {t("preview.cta")}
          </Button>
        </Grid>

        <Grid size={{ xs: 12, md: 7 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              mb: 1.5,
            }}
          >
            {t("auth.featuresTitle")}
          </Typography>
          <Typography
            sx={{
              color: "text.secondary",
              mb: 3,
            }}
          >
            {t("auth.featuresSubtitle")}
          </Typography>
          <Grid container spacing={2.5}>
            {features.map((feature) => (
              <Grid size={{ xs: 12, sm: 6 }} key={feature.title}>
                <Box
                  sx={{
                    p: 2.5,
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor: "divider",
                    height: "100%",
                    bgcolor: "background.paper",
                  }}
                >
                  <Box sx={{ mb: 1.5 }}>{feature.icon}</Box>
                  <Typography
                    variant="subtitle1"
                    gutterBottom
                    sx={{
                      fontWeight: 600,
                    }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                    }}
                  >
                    {feature.desc}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
}
